import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Button, Typography, Box, Collapse, IconButton } from "@mui/material";
import { makeStyles } from '@mui/styles';
import { GridColDef, GridRowModel, GridRowId, DataGrid, GridPagination } from '@mui/x-data-grid';
import DateCell from "../../../../components/DateCell";
import useActionItems, { WorkListTableData } from "../../hooks/useActionItems";
import { useEffect, useState } from "react";
import customCheckBoxColumn from "../../../../components/CustomCheckboxColumn/CustomCheckboxColumn";
import TablePaginated from "../../../Manage/components/TablePaginated/TablePaginated";
import LoadingButton from "@mui/lab/LoadingButton";
import LaunchIcon from "@mui/icons-material/Launch"
import { useManageModal } from "../../../Manage/components/ManageModal";
import { useMutation } from "@apollo/client";
import usePersonAuthStore, { decodedToken } from "../../../store/personAuthStore"
import usePersonStore from "../../../store/personStore";
import { markReviewed } from "../../../../features/graphql/mutation/markReviewed/markReviewed";
const useStyles = makeStyles({
    root: {
        '& .MuiDataGrid-autoHeight': {
            minHeight: "212px"
        },
        '& .results-grid': {
            backgroundColor: "#F03F3F",
        },
        '& .results-grid--Severe': {
            backgroundColor: "#FEE2E2",
        },
        '& .MuiDataGrid-columnsContainer': {
            backgroundColor: "#8E1C3B",
            color: "#fff"
        },
        "& .MuiDataGrid-root": {
            background: "#fff",
            border: "none",
            boxShadow: "rgb(0 0 0 / 20%) 0px 2px 4px -1px, rgb(0 0 0 / 14%) 0px 4px 5px 0px, rgb(0 0 0 / 12%) 0px 1px 10px 0px"
        },
        "& .MuiDataGrid-columnSeparator": {
            display: "none"
        },
        "& .MuiDataGrid-sortIcon": {
            color: "#fff"
        },
        "& .MuiDataGrid-menuIconButton": {
            color: "#fff"
        },
        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none",
        },
        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
        {
            outline: "none",
        },
    },
});
const DEFAULT_PAGE_SIZE = 100;

export interface RiskWarningGridProps {
    onClickResults: (value: any) => void
}

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}
function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {
    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

function RiskWarningGrid({ onClickResults }: RiskWarningGridProps) {
    const classes = useStyles();
    const { openModal, closeModal } = useManageModal();
    const { actionItems, refetch } = useActionItems();
    const [MarkReviewed, { loading }] = useMutation(markReviewed);
    const accessToken = usePersonAuthStore((state) => state.access_token);
    const defaultLocation = usePersonStore(state => state.company.defaultLocation);
    const [buttonSettings, setButtonSettings] = useState({
        disabled: true,
        saving: false
    })
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[];
        rows: GridRowModel[];
        selectionModel: (string | number)[];
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        };
    }>({
        columns: [],
        rows: [],
        selectionModel: [],
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
    });

    const RISK_WARNING_COLUMNS: GridColDef[] = [{
        field: 'createdAt',
        headerName: "Date of Session",
        headerAlign: "center",
        flex: 1,
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "center", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>Date of Session</div>
        ),
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ value }) => {
            return value ?
                <div style={{ textAlign: "center" }}> <DateCell value={value.toString()} type={"session"} /></div> : '--'
        }
    },
    {
        field: 'name', headerName: "Name",
        flex: 3,
        sortable: false,
        disableColumnMenu: true,
    },
    {
        field: 'reason', headerName: "Reason",
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
    },
    {
        field: "summary",
        headerName: "View Summary",
        sortable: false,
        flex: 1,
        align: "center",
        disableColumnMenu: true,
        headerAlign: "center",
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", fontWeight: 500, lineHeight: 1, textAlign: "center", width: "100%" }}>View Summary</div>
        ),
        renderCell: (params) => {
            const onClick = () => {
                onClickResults(params.row)
            };
            return <IconButton onClick={onClick}><FileCopyIcon /></IconButton>;
        }
    },]
    useEffect(() => {
        setPageTableState((prevState) => {
            const columns = [...RISK_WARNING_COLUMNS]
            columns.unshift(
                customCheckBoxColumn({
                    title: "Mark as Read",
                    selectionModel: pageTableState.selectionModel,
                    handleSelectionModelChange: handleSelectionModelChange,
                    handleSelectAll: handleSelectAll,
                    totalRowCount: actionItems.filter((item: WorkListTableData) => isRowSelectable(item)).length
                })
            )
            const rows = actionItems
            setPageTableState((prevState) => ({
                ...prevState,
                rows
            }))
            return { ...prevState, columns }
        })
    }, [actionItems, pageTableState.selectionModel])

    const handleSelectAll = (selectAll: boolean) => {
        if (selectAll) {
            const selectableRows = pageTableState.rows.filter((row) => isRowSelectable(row))
            const allIds = selectableRows?.map((row) => row.id as GridRowId);
            handleSelectionModelChange(allIds)
            // handleSelectionModelChange({ type: 'include', ids: new Set(allIds) });
            setButtonSettings((prevState) => ({ ...prevState, disabled: false }))
        } else {
            handleSelectionModelChange([])
            // handleSelectionModelChange({ type: 'include', ids: new Set() });
            setButtonSettings((prevState) => ({ ...prevState, disabled: true }))
        }
    }
    const isRowSelectable = (worklist: any): boolean => {
        return worklist.reason === "Unread" ? true : false
    }
    const handleCancelMarkAsRead = () => {
        handleSelectionModelChange([])
        // handleSelectionModelChange({ type: 'include', ids: new Set() });
        setButtonSettings((prevState) => ({ ...prevState, disabled: true }))
    }
    const handleSubmitMarkAsRead = () => {
        setButtonSettings((prevState) => ({ ...prevState, saving: true }))
        const _worklists = Array.from(pageTableState.selectionModel)
        const markWorklists = () => {
            _worklists.forEach(async (worklistId) => {
                await MarkReviewed({ variables: { id: worklistId } })
            })
            return new Promise((res, rej) => {
                setTimeout(() => {
                    res("Operation Complete")
                }, 1000)
            })
        }
        markWorklists().then((result) => {
            setButtonSettings((prevState) => ({ ...prevState, saving: false }))
            handleSelectionModelChange([])
            // handleSelectionModelChange({ type: 'include', ids: new Set() });
            refetch()
        });
    }

    const handleSelectionModelChange = (values: (string | number)[]) => {
        const selectedIds = Array.from(values)
        const prevSelectedIds = Array.from(pageTableState.selectionModel);

        const assign = selectedIds.filter(x => !prevSelectedIds.includes(x));
        const unassign = prevSelectedIds.filter(x => !selectedIds.includes(x));
        const uniqAssign = assign.filter(x => !pageTableState.selections.prevAssign.includes(x));
        setPageTableState(prevState => {
            const nextAssign = unique(deltaSelections(uniqAssign, unassign, prevState.selections.nextAssign));
            const nextUnassign = deltaSelections(unassign, assign, prevState.selections.unassign);
            const selectionModel = {
                ...prevState,
                selectionModel: values,
                selections: {
                    ...prevState.selections,
                    nextAssign,
                    unassign: nextUnassign,
                    allSelected: unique(selectedIds.concat(nextAssign).filter(x => x !== unassign[0]))
                }
            }
            return selectionModel;
        })
    }
    const handleTriage = async () => {
        const sessionIds = actionItems.map((x: any) => x.sessionId)
        if (accessToken) {
            openModal({ type: "UNREAD_TRIAGE_GENERATION_INITIALIZED", eventType: "DISABLE_BACKDROP", cancelButtonLabel: "Close" })
            const { pid, lid } = decodedToken(accessToken);
            const response = await fetch(`${window.__env.API_URL}/api/triage/create-unread`, {
                method: "POST",
                headers: {
                    Pid: pid,
                    Lid: defaultLocation!.id ?? lid,
                    "Content-Type": "application/json",
                    authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    sessionIds: sessionIds
                }),

            })
            if (response.ok) {
                const fileId = await response.json()
                closeModal()
                const link = document.createElement('a');
                link.href = `${window.__env.API_URL}/api/file/download/client/triage-reports/${fileId.fileId}`
                link.target = "_blank"
                // TODO: MAKE A LOADING PAGE AND STATE FOR THIS
                // Prevent potential memory leak and cleanup ObjectURL
                // resources that are no longer in use
                const linkHandler = () => {
                    setTimeout(() => {
                        link.removeEventListener('click', linkHandler);
                        document.body.removeChild(link);
                    }, 300);
                }
                link.addEventListener('click', linkHandler);
                document.body.appendChild(link);
                link.click()
            } else {
                openModal({ type: "GENERAL_ERROR" })
            }
        }
    }
    useEffect(() => {
        if (pageTableState.selectionModel.length > 0) {
            setButtonSettings((prevState) => ({ ...prevState, disabled: false }))
        } else {
            setButtonSettings((prevState) => ({ ...prevState, disabled: true }))
        }
    }, [pageTableState.selectionModel])
    return (
        <Collapse unmountOnExit={false} in={Boolean(actionItems.length)} timeout={250} collapsedSize={0} orientation="vertical">
            <Box component="div" sx={{ width: "100%" }} className={classes.root}>
                <DataGrid
                    sx={{
                        ".MuiDataGrid-columnHeader": { backgroundColor: "#8E1C3B", color: "White", },
                        ".MuiDataGrid-columnHeaders": { borderTopLeftRadius: "0px", borderTopRightRadius: "0px" },
                        ".MuiDataGrid-columnSeparator": { display: "none" },
                        ".MuiDataGrid-filler": { display: "none !important" },
                        '& .MuiDataGrid-scrollbarFiller': {
                            display: 'none !important',
                        },
                        backgroundColor: "White"
                    }}
                    rows={pageTableState.rows}
                    loading={false}
                    columns={pageTableState.columns}
                    slots={{
                        toolbar: () => (<ActionsNeededToolbar
                            title={`Actions Needed`}
                            onClick={handleTriage}
                        />),
                        footer: () => (
                            <ActionsNeededFooter
                                disabled={buttonSettings.disabled}
                                saving={buttonSettings.saving}
                                onCancel={handleCancelMarkAsRead}
                                onSubmit={handleSubmitMarkAsRead} />
                        )
                    }}
                    showToolbar
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 6 }
                        }
                    }}
                    pageSizeOptions={[6]}
                />
            </Box>
        </Collapse>
    );
}
type ActionsNeededProps = {
    title: string,
    onClick: () => {}
}
const ActionsNeededToolbar = ({ title, onClick }: ActionsNeededProps) => {
    return (
        <Box component="div"
            sx={{
                padding: "8px 16px", bgcolor: "#8E1C3B", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
            <Typography variant="h4" sx={{ textAlign: "left" }}>
                {title}
            </Typography>
            <Button
                onClick={onClick}
                sx={{ color: "white!important", maxWidth: "225px" }}
                startIcon={<LaunchIcon />}>Get Triage Report of Unread Sessions</Button>
        </Box>
    )
}
type ActionsNeededFooterProps = {
    onSubmit?: () => void,
    disabled: boolean,
    saving: boolean,
    onCancel?: () => void
}
const ActionsNeededFooter = ({ onSubmit, disabled, saving, onCancel }: ActionsNeededFooterProps) => {
    return (
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box component="div" sx={{ padding: "2px 16px", display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                <Button onClick={onCancel}>Cancel</Button>
                <LoadingButton loading={saving}
                    disableElevation
                    disabled={disabled}
                    variant="contained"
                    size="large"
                    sx={{
                        fontSize: ".875rem",
                        height: "fit-content",
                        color: "black",
                        marginLeft: ".5rem",
                        borderRadius: "4px",
                        backgroundColor: "rgb(131,164,123)",
                        fontWeight: "bold",
                        padding: ".5rem",
                        "&:hover": { backgroundColor: "rgb(86,117,79)" }
                    }}
                    onClick={onSubmit}
                >Save</LoadingButton>
            </Box>
            <GridPagination />
        </Box>
    )
}
export default RiskWarningGrid
